import { Container, Sprite, Texture } from "pixi.js";
import { NULL_LOG, type Log } from "../../log/Log";
import type { SymbolDefinitionResolver, SymbolDefinition } from "../../symbols/SymbolDefinition";
import { SymbolFitMode } from "../../symbols/SymbolFitMode";

/** Container → the SymbolView that owns it (root + content), for effects handed a bare node. */
const viewByContainer = new WeakMap<Container, SymbolView>();

/**
 * Resolve the `SymbolView` from whatever container an effect port hands over (the cell root OR
 * its content child — ports disagree by design). Walks up a few parents, like the original
 * `getComponent(SymbolView)` walk did.
 */
export function resolveSymbolView(node: Container | null | undefined): SymbolView | null {
  let current: Container | null = node ?? null;
  for (let depth = 0; current && !current.destroyed && depth < 4; depth++) {
    const view = viewByContainer.get(current);
    if (view) return view;
    current = current.parent;
  }
  return null;
}

/**
 * One grid cell's visual. Mirrors `SymbolView.prefab`:
 *
 *   node (cell root, positioned by the grid at the cell center)
 *     └ contentNode (scaled to fit the cell; effects animate / attach here)
 *         └ symbolSprite (anchor 0.5; the resting sprite)
 *
 * The original component sat on the root node and referenced the two children; here
 * the class OWNS the three containers so ported code keeps reading `view.node`,
 * `view.contentNode`, `view.symbolSprite`.
 */
export class SymbolView {
  public readonly node: Container;
  public readonly contentNode: Container;
  public readonly symbolSprite: Sprite;

  private symbolId: string | null = null;
  private landSoundPlayed: boolean = false;

  private resolver: SymbolDefinitionResolver | null = null;
  private currentDefinition: SymbolDefinition | null = null;

  private cellWidth: number = 0;
  private cellHeight: number = 0;

  private poolUnsafe: boolean = false;

  public constructor(private readonly log: Log = NULL_LOG) {
    this.node = new Container({ label: "SymbolView" });
    this.contentNode = new Container({ label: "content" });
    this.symbolSprite = new Sprite({ texture: Texture.EMPTY, anchor: 0.5, label: "Sprite" });
    this.contentNode.addChild(this.symbolSprite);
    this.node.addChild(this.contentNode);
    viewByContainer.set(this.node, this);
    viewByContainer.set(this.contentNode, this);
  }

  public setDefinitionResolver(resolver: SymbolDefinitionResolver | null): void {
    this.resolver = resolver;
  }

  public setCellSizeForFit(width: number, height: number): void {
    this.cellWidth = width;
    this.cellHeight = height;
  }

  public getSymbolId(): string | null {
    return this.symbolId;
  }

  public getCurrentDefinition(): SymbolDefinition | null {
    return this.currentDefinition;
  }

  public getCurrentLandSoundKey(): string | null {
    return (this.currentDefinition?.landSoundKey ?? "").trim() || null;
  }

  /**
   * Whether this symbol's land sound suppresses the default `column_drop` thud
   * (vs. playing alongside it). Default true. Only meaningful when the symbol
   * also has a land-sound key.
   */
  public getCurrentSuppressDefaultDropSound(): boolean {
    return this.currentDefinition?.suppressDefaultDropSound ?? true;
  }

  public hasPlayedLandSound(): boolean {
    return this.landSoundPlayed;
  }

  public markLandSoundPlayed(): void {
    this.landSoundPlayed = true;
  }

  public resetLandSoundPlayed(): void {
    this.landSoundPlayed = false;
  }

  /**
   * Called by the grid renderer (via GridSymbolPool) when binding the symbol from a
   * decoded grid snapshot. Generic: sets the id, resets land-sound state,
   * and applies the resolved sprite/scale via the catalog. An id with no definition
   * empties the sprite (`Texture.EMPTY`) and logs, so a pooled view never shows the
   * symbol it was last bound to. The pool fires its symbolIdHook after this method returns.
   */
  public setSymbolId(symbolId: string): void {
    this.symbolId = symbolId;
    this.resetLandSoundPlayed();
    this.currentDefinition = null;
    this.symbolSprite.position.set(0, 0);

    const def = this.resolver?.resolveDefinition(symbolId) ?? null;
    if (!def) {
      // An id the catalog does not know shows an empty cell, never the symbol this pooled view
      // showed last. The texture, not visibility: the bind hook and the Spine host re-show the
      // sprite on every bind.
      this.symbolSprite.texture = Texture.EMPTY;
      this.log(`[SymbolView] no SymbolDefinition for symbolId="${symbolId}"`);
      return;
    }

    this.applyDefinition(def);
  }

  public applyDefinition(def: SymbolDefinition): void {
    this.currentDefinition = def;
    this.symbolSprite.texture = def.texture;

    const fitScale = this.computeFitScale(def.fitMode);
    const finalScale = fitScale * (Number.isFinite(def.baseScale) ? def.baseScale : 1);

    this.contentNode.scale.set(finalScale, finalScale);

    const ox = def.offsetX ?? 0;
    const oy = def.offsetY ?? 0;
    if (finalScale > 0) {
      this.symbolSprite.position.set(ox / finalScale, oy / finalScale);
    }
  }

  private computeFitScale(fitMode: SymbolFitMode): number {
    if (fitMode === SymbolFitMode.None) return 1;
    if (this.cellWidth <= 0 || this.cellHeight <= 0) return 1;

    // Native (untrimmed) pixel size of the frame.
    const texture = this.symbolSprite.texture;
    const spriteW = Math.max(1, texture.orig.width);
    const spriteH = Math.max(1, texture.orig.height);

    if (fitMode === SymbolFitMode.MatchWidth) return this.cellWidth / spriteW;
    if (fitMode === SymbolFitMode.MatchHeight) return this.cellHeight / spriteH;
    if (fitMode === SymbolFitMode.Contain) return Math.min(this.cellWidth / spriteW, this.cellHeight / spriteH);

    return 1;
  }

  /** Call when this instance has been structurally altered and must not re-enter pool. */
  public markPoolUnsafe(): void {
    this.poolUnsafe = true;
  }

  public isPoolReusable(): boolean {
    return !this.poolUnsafe;
  }

  /** Destroy the display tree (pool-unsafe release / teardown). */
  public destroy(): void {
    if (!this.node.destroyed) this.node.destroy({ children: true });
  }
}
